// Scan sessions for the ops phone (05 §5.2): pick a trip or container, scan many boxes, see a
// running list, finish. The engine here keeps each session on the device (IndexedDB, never
// localStorage) and turns its scans into as few commands as it can (02 §2.3, Write load):
//
//  - online and nothing waiting to sync: one command per ONLINE_BATCH boxes or FLUSH_MS, whichever
//    comes first, so the trip or container sees about one write every few seconds;
//  - offline, or the last command has not reached the server: keep collecting, and send one command
//    per MAX_BATCH (200, the most a command carries) or when the person finishes. A session of 450
//    boxes with no signal makes three commands, not 450. The scans are saved on the phone as they
//    are made, so a crash or a flat battery loses nothing;
//  - every command carries the time of its first scan, not the time it was sent.
//
// The server is the authority. This engine only decides when to send, never whether a box is
// allowed: boxes the phone already knows will be refused are held back until the person says
// "send anyway", and every refusal the server reports is shown against its box.
import { createStore as createIdbStore, del, entries, set } from 'idb-keyval';
import { ulid } from 'ulid';
import { barcodeSchema, unitIdSchema, type CommandType } from '@ecobox/schema';
import type { CommandWriter } from './commands';
import type { Connectivity } from './connectivity';
import { isUnsentState, type LedgerEntry, type LedgerState } from './ledger';
import type { Precheck } from './ops-movement';
import { createStore, type ReadableStore } from './store';

// ---- tuning --------------------------------------------------------------------

/** The most barcodes one command carries (the schema allows 200). */
export const MAX_BATCH = 200;
/** Online, a batch goes as soon as it holds this many boxes (02 §2.3). */
export const ONLINE_BATCH = 25;
/** Online, a batch goes this long after its first scan. */
export const FLUSH_MS = 5_000;
const TICK_MS = 1_000;
const KEEP_FINISHED_MS = 2 * 24 * 60 * 60 * 1000;

// ---- the kinds of session ---------------------------------------------------------

export type SessionKind =
  | 'load_trip'
  | 'receive_trip'
  | 'load_container'
  | 'receive_container'
  | 'load_handover'
  | 'run_load'
  | 'run_return';

export interface SessionKindInfo {
  label: string;
  /** What the person is scanning against. */
  carrier: 'trip' | 'container' | 'handover' | 'run';
  /** The command that carries the scans. */
  scan: Extract<
    CommandType,
    | 'loadTrip'
    | 'receiveTrip'
    | 'loadContainer'
    | 'receiveContainer'
    | 'loadHandover'
    | 'startRun'
    | 'closeRun'
  >;
  /** Takes a scanned box off again (loading only). */
  undo?: Extract<CommandType, 'unloadFromTrip' | 'removeFromContainer' | 'removeFromHandover'>;
  /** The payload key that names the trip, container, handover or run. */
  idKey: 'tripId' | 'containerId' | 'handoverId' | 'runId';
  /** The payload key that lists the scanned codes. */
  itemsKey: 'barcodes' | 'unitIds' | 'barcodesBack';
  /**
   * `handover` is the hub's hand-over to a partner: its own screen, not Load or Receive. `run`
   * is a driver's loading scan or scan-back: the driver screens, not Load or Receive.
   */
  direction: 'load' | 'receive' | 'handover' | 'run';
  /**
   * Keep scans on the phone until the person finishes (or 200 are waiting), instead of sending a
   * batch every few seconds. A handover needs this so damage can be noted on a unit before the
   * command that carries its note leaves.
   */
  holdUntilFinish?: true;
  /** Prime pieces (`DXB260000013-P1`) are scanned as well as box barcodes. */
  acceptsPieces?: true;
  /**
   * The whole session is ONE command, sent only when the person finishes (a run starts once and
   * closes once). Nothing goes before that, not even at 200 scans, and `maxScans` is the most
   * the command carries, so a second `startRun` or `closeRun` is never made.
   */
  singleCommand?: true;
  /** With `singleCommand`: the most scans the session takes (the schema's limit for the command). */
  maxScans?: number;
  /** Extra payload fields from the session's `extra` (a run's end-of-run note). */
  extraPayload?: (session: OpsSession) => Record<string, unknown>;
}

export const SESSION_KINDS: Record<SessionKind, SessionKindInfo> = {
  load_trip: {
    label: 'Load trip',
    carrier: 'trip',
    scan: 'loadTrip',
    undo: 'unloadFromTrip',
    idKey: 'tripId',
    itemsKey: 'barcodes',
    direction: 'load',
  },
  receive_trip: {
    label: 'Receive trip',
    carrier: 'trip',
    scan: 'receiveTrip',
    idKey: 'tripId',
    itemsKey: 'barcodes',
    direction: 'receive',
  },
  load_container: {
    label: 'Load container',
    carrier: 'container',
    scan: 'loadContainer',
    undo: 'removeFromContainer',
    idKey: 'containerId',
    itemsKey: 'barcodes',
    direction: 'load',
  },
  receive_container: {
    label: 'Receive container',
    carrier: 'container',
    scan: 'receiveContainer',
    idKey: 'containerId',
    itemsKey: 'barcodes',
    direction: 'receive',
  },
  load_handover: {
    label: 'Hand over',
    carrier: 'handover',
    scan: 'loadHandover',
    undo: 'removeFromHandover',
    idKey: 'handoverId',
    itemsKey: 'unitIds',
    direction: 'handover',
    holdUntilFinish: true,
    acceptsPieces: true,
  },
  // The driver's loading scan: one `startRun` with every box that went on the van (04 §4.5).
  run_load: {
    label: 'Load the van',
    carrier: 'run',
    scan: 'startRun',
    idKey: 'runId',
    itemsKey: 'barcodes',
    direction: 'run',
    holdUntilFinish: true,
    singleCommand: true,
    maxScans: 200,
  },
  // The driver's scan-back: one `closeRun` with every undelivered box that came back.
  run_return: {
    label: 'End the run',
    carrier: 'run',
    scan: 'closeRun',
    idKey: 'runId',
    itemsKey: 'barcodesBack',
    direction: 'run',
    holdUntilFinish: true,
    singleCommand: true,
    maxScans: 400,
    extraPayload: (session) => ({ note: (session.extra?.['note'] ?? '').slice(0, 300) }),
  },
};

/** Longest damage note the server keeps for one unit (`conditionNotes` in `loadHandover`). */
export const CONDITION_NOTE_MAX = 300;

// ---- the stored session -----------------------------------------------------------

export type ScanStatus =
  /** Not sent yet; goes in the next command. */
  | 'buffered'
  /** In a command (`commandId`); its fate is read from the device ledger. */
  | 'queued'
  /** The phone expects the server to refuse it, so it is held back until "send anyway". */
  | 'held'
  /** Not an EcoBox box barcode. Never sent. */
  | 'invalid';

export interface SessionScan {
  barcode: string;
  /** ISO time of the scan on this phone. */
  at: string;
  status: ScanStatus;
  commandId?: string;
  /** Why the phone is warning about it, before the server has spoken. */
  warning?: string;
  /** What is wrong with the unit itself (a handover's damage note). Travels with the scan's command. */
  note?: string;
}

export interface SessionBatch {
  commandId: string;
  type: CommandType;
  count: number;
  at: string;
}

export interface OpsSession {
  id: string;
  kind: SessionKind;
  /** Trip or container id. */
  targetId: string;
  /** What the person sees: "Trip to MNL", "TCLU 123456-7". */
  targetLabel: string;
  uid: string;
  branchId: string;
  startedAt: string;
  updatedAt: string;
  scans: SessionScan[];
  batches: SessionBatch[];
  /** The command that closed or departed the carrier, once sent. */
  closeCommandId?: string;
  /** Set when the person is done with it (closed, or walked away on purpose). */
  finishedAt?: string;
  lastError?: string;
  /** Facts a screen needs to pick the session up again, such as a handover's `partnerId`. */
  extra?: Record<string, string>;
}

// ---- storage -----------------------------------------------------------------------

export interface SessionStorage {
  loadAll(): Promise<OpsSession[]>;
  save(session: OpsSession): Promise<void>;
  remove(id: string): Promise<void>;
}

/** IndexedDB, one record per session. */
export function createIdbSessionStorage(dbName = 'ecobox-ops-sessions'): SessionStorage {
  const store = createIdbStore(dbName, 'sessions');
  return {
    async loadAll() {
      const rows = await entries<string, OpsSession>(store);
      return rows.map(([, session]) => session);
    },
    save: (session) => set(session.id, session, store),
    remove: (id) => del(id, store),
  };
}

export function createMemorySessionStorage(initial: readonly OpsSession[] = []): SessionStorage {
  const map = new Map(initial.map((s) => [s.id, s]));
  return {
    loadAll: () => Promise.resolve([...map.values()]),
    save(session) {
      map.set(session.id, structuredClone(session));
      return Promise.resolve();
    },
    remove(id) {
      map.delete(id);
      return Promise.resolve();
    },
  };
}

// ---- reading the server's answer ------------------------------------------------------

/**
 * Per-box refusals the server reports in a command's `result`. The handler may refuse some boxes
 * and apply the rest; those come back as `result.refused` (or `refusals`) = `[{ barcode, reason }]`.
 * A command the server rejects as a whole has no such list: every box of it shares the error.
 * ASSUMPTION to verify against the movement handlers (see the report).
 */
export function readRefusals(result: Record<string, unknown> | undefined): Map<string, string> {
  const out = new Map<string, string>();
  if (!result) return out;
  for (const key of ['refused', 'refusals']) {
    const list = result[key];
    if (!Array.isArray(list)) continue;
    for (const item of list as unknown[]) {
      if (typeof item === 'string') {
        out.set(item, 'Refused by the server');
      } else if (typeof item === 'object' && item !== null) {
        const r = item as Record<string, unknown>;
        const code =
          typeof r.barcode === 'string'
            ? r.barcode
            : typeof r.unitId === 'string'
              ? r.unitId
              : undefined;
        const why = [r.message, r.reason, r.code].find((v) => typeof v === 'string' && v !== '');
        if (code) out.set(code, typeof why === 'string' ? why : 'Refused by the server');
      }
    }
  }
  return out;
}

export type ScanTone = 'ok' | 'wait' | 'warn' | 'bad';

export interface ScanView {
  tone: ScanTone;
  label: string;
  detail?: string;
  /** Counts as a good scan in the running total. */
  counts: boolean;
}

/** How one scan reads on screen: its own state, then its command's fate in the device ledger. */
export function viewScan(scan: SessionScan, entry: LedgerEntry | undefined): ScanView {
  switch (scan.status) {
    case 'invalid':
      return { tone: 'bad', label: 'Not a box barcode', counts: false };
    case 'held':
      return {
        tone: 'warn',
        label: 'Held back',
        ...(scan.warning ? { detail: scan.warning } : {}),
        counts: false,
      };
    case 'buffered':
      return {
        tone: 'wait',
        label: 'Not sent yet',
        ...(scan.warning ? { detail: scan.warning } : {}),
        counts: true,
      };
    case 'queued':
      break;
  }
  const warning = scan.warning ? { detail: scan.warning } : {};
  if (!entry) return { tone: 'wait', label: 'Queued', ...warning, counts: true };
  switch (entry.state) {
    case 'pending':
      return { tone: 'wait', label: 'Waiting to sync', ...warning, counts: true };
    case 'synced':
      return { tone: 'wait', label: 'Sent, awaiting result', ...warning, counts: true };
    case 'not_delivered':
      return {
        tone: 'bad',
        label: 'Not delivered',
        detail: entry.lastError?.message ?? 'The server has not received it. Resend from Me.',
        counts: true,
      };
    case 'rejected':
      return {
        tone: 'bad',
        label: 'Refused by the server',
        detail: entry.lastError?.message ?? 'The server rejected this command.',
        counts: false,
      };
    case 'applied':
    case 'applied_with_exception': {
      const refused = readRefusals(entry.result).get(scan.barcode);
      if (refused !== undefined) {
        return { tone: 'bad', label: 'Refused by the server', detail: refused, counts: false };
      }
      return entry.state === 'applied'
        ? { tone: 'ok', label: 'Done', ...warning, counts: true }
        : {
            tone: 'warn',
            label: 'Done, needs review',
            detail: scan.warning ?? 'The server raised an exception for a person to check.',
            counts: true,
          };
    }
  }
}

export interface SessionCounts {
  /** Good scans: sent or waiting, not refused. */
  good: number;
  refused: number;
  held: number;
  invalid: number;
  /** Scans not yet saved on the server. */
  waiting: number;
  /** Scans on this phone not yet made into a command. */
  unsent: number;
}

export function countSession(
  session: OpsSession,
  ledger: ReadonlyMap<string, LedgerEntry>,
): SessionCounts {
  const counts: SessionCounts = { good: 0, refused: 0, held: 0, invalid: 0, waiting: 0, unsent: 0 };
  for (const scan of session.scans) {
    const view = viewScan(scan, scan.commandId ? ledger.get(scan.commandId) : undefined);
    if (scan.status === 'invalid') counts.invalid += 1;
    else if (scan.status === 'held') counts.held += 1;
    else if (view.counts) counts.good += 1;
    else counts.refused += 1;
    if (scan.status === 'buffered') counts.unsent += 1;
    if (view.tone === 'wait') counts.waiting += 1;
  }
  return counts;
}

export function ledgerById(entries: readonly LedgerEntry[]): Map<string, LedgerEntry> {
  return new Map(entries.map((e) => [e.id, e]));
}

// ---- the engine -------------------------------------------------------------------------

export type AddScanOutcome = 'added' | 'held' | 'invalid' | 'duplicate';

export interface AddScanResult {
  outcome: AddScanOutcome;
  barcode: string;
  /** Plain words for the screen and the camera overlay. */
  message: string;
  tone: 'ok' | 'warn' | 'bad';
}

export interface StartSessionInput {
  kind: SessionKind;
  targetId: string;
  targetLabel: string;
  uid: string;
  branchId: string;
  /** Kept with the session (see `OpsSession.extra`). */
  extra?: Record<string, string>;
}

export interface SessionEngine extends ReadableStore<readonly OpsSession[]> {
  /** Resolves when sessions saved by earlier runs have been loaded. */
  readonly ready: Promise<void>;
  /** Resolves when every change has reached IndexedDB. */
  idle(): Promise<void>;
  get(id: string): OpsSession | undefined;
  /** Starts a session, or returns the one already in progress for the same person and carrier. */
  start(input: StartSessionInput): OpsSession;
  addScan(id: string, raw: string, precheck?: Precheck): AddScanResult;
  /** Sends a held box after all ("send anyway"). */
  releaseHeld(id: string, barcode: string): void;
  /**
   * Writes (or, with an empty note, clears) the damage note of a scan that has not been sent yet.
   * Returns false when the scan is already in a command: the note could not travel with it.
   */
  setNote(id: string, barcode: string, note: string): boolean;
  /** Takes a box off the list; if it was already sent, sends the matching undo command. */
  removeScan(id: string, barcode: string): void;
  /** Keeps a fact with the session (a run's end-of-run note) until its command is made. */
  setExtra(id: string, key: string, value: string): void;
  /** Turns the waiting scans into commands now. Returns how many commands were sent. */
  flush(id: string): number;
  /** `flush` for every session of this person: before sign-out. */
  flushAll(uid: string): number;
  /** After a command was refused as a whole: send its boxes one by one to find the culprit. */
  retrySingly(id: string, commandId: string): number;
  /** The carrier was closed or departed by this command. */
  markClosed(id: string, commandId: string): void;
  /** The person is done. The session leaves "in progress". */
  complete(id: string): void;
  /** Takes a finished session back into progress: the closing command was refused and the person tries again. */
  reopen(id: string): void;
  /** Drops a session that has nothing unsent in it. Returns false if it could not. */
  discard(id: string): boolean;
  /** Forgets every session of one person: after a safe sign-out, so a shared phone starts clean. */
  clearUser(uid: string): void;
  /** Check whether anything should go now. Runs by itself; exposed for tests. */
  tick(): void;
  dispose(): void;
}

export interface SessionEngineDeps {
  writer: Pick<CommandWriter, 'send' | 'getSnapshot' | 'subscribe'>;
  connectivity: Pick<Connectivity, 'getSnapshot' | 'subscribe'>;
  storage?: SessionStorage;
  now?: () => number;
  maxBatch?: number;
  onlineBatch?: number;
  flushMs?: number;
  /** Set false in tests that drive `tick()` by hand. */
  autoTick?: boolean;
}

function unsentCommandIds(entries: readonly LedgerEntry[]): Set<string> {
  const ids = new Set<string>();
  for (const e of entries) if (isUnsentState(e.state as LedgerState)) ids.add(e.id);
  return ids;
}

export function isInProgress(session: OpsSession): boolean {
  return session.finishedAt === undefined;
}

export function createSessionEngine(deps: SessionEngineDeps): SessionEngine {
  const {
    writer,
    connectivity,
    storage = createIdbSessionStorage(),
    now = Date.now,
    maxBatch = MAX_BATCH,
    onlineBatch = ONLINE_BATCH,
    flushMs = FLUSH_MS,
    autoTick = true,
  } = deps;

  const map = new Map<string, OpsSession>();
  const store = createStore<readonly OpsSession[]>([]);
  const dirty = new Set<string>();
  const removed = new Set<string>();
  let draining: Promise<void> | undefined;
  // True while a command is being handed to the writer: the ledger tells us about it on the same
  // call stack, and that must not start a second flush of the same scans.
  let sending = false;
  let timer: ReturnType<typeof setInterval> | undefined;

  const publish = (): void =>
    store.set(
      [...map.values()].sort((a, b) =>
        a.startedAt === b.startedAt
          ? a.id.localeCompare(b.id)
          : a.startedAt.localeCompare(b.startedAt),
      ),
    );

  const drain = (): Promise<void> => {
    draining ??= (async () => {
      try {
        while (dirty.size > 0 || removed.size > 0) {
          const [id] = [...removed, ...dirty];
          if (id === undefined) break;
          try {
            if (removed.delete(id)) await storage.remove(id);
            else {
              dirty.delete(id);
              const session = map.get(id);
              if (session) await storage.save(session);
            }
          } catch (err) {
            console.error('A scan session could not be saved on this phone.', err);
          }
        }
      } finally {
        draining = undefined;
      }
    })();
    return draining;
  };

  const save = (session: OpsSession): OpsSession => {
    const next = { ...session, updatedAt: new Date(now()).toISOString() };
    map.set(next.id, next);
    dirty.add(next.id);
    publish();
    void drain();
    return next;
  };

  const update = (id: string, change: (s: OpsSession) => OpsSession): OpsSession | undefined => {
    const current = map.get(id);
    return current ? save(change(current)) : undefined;
  };

  const ready = (async () => {
    try {
      const saved = await storage.loadAll();
      const t = now();
      for (const session of saved) {
        const old =
          session.finishedAt !== undefined && t - Date.parse(session.finishedAt) > KEEP_FINISHED_MS;
        if (old) {
          removed.add(session.id);
        } else if (!map.has(session.id)) {
          map.set(session.id, session);
        }
      }
      publish();
      void drain();
    } catch (err) {
      console.error('Scan sessions could not be loaded.', err);
    }
  })();

  const isOnline = (): boolean => connectivity.getSnapshot().online;

  /** Sends `scans` as commands of at most `maxBatch`. Returns the commands sent. */
  const sendScans = (session: OpsSession, scans: SessionScan[]): number => {
    const info = SESSION_KINDS[session.kind];
    let sent = 0;
    let current = session;
    const size = info.singleCommand ? (info.maxScans ?? maxBatch) : maxBatch;
    for (let i = 0; i < scans.length; i += size) {
      const chunk = scans.slice(i, i + size);
      const first = chunk[0];
      if (!first) break;
      let commandId: string;
      sending = true;
      try {
        const notes = Object.fromEntries(
          chunk.flatMap((s) => (s.note ? [[s.barcode, s.note]] : [])),
        );
        const result = writer.send(
          info.scan,
          {
            [info.idKey]: session.targetId,
            [info.itemsKey]: chunk.map((s) => s.barcode),
            ...(info.scan === 'loadHandover' ? { conditionNotes: notes } : {}),
            ...(info.extraPayload ? info.extraPayload(session) : {}),
          },
          { occurredAtMs: Date.parse(first.at) },
        );
        commandId = result.id;
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        sending = false;
        save({ ...current, lastError: message });
        return sent;
      }
      sending = false;
      sent += 1;
      const ids = new Set(chunk.map((s) => s.barcode));
      const { lastError: _cleared, ...rest } = current;
      current = save({
        ...rest,
        scans: current.scans.map((s) =>
          ids.has(s.barcode) && s.status === 'buffered'
            ? { ...s, status: 'queued' as const, commandId }
            : s,
        ),
        batches: [
          ...current.batches,
          { commandId, type: info.scan, count: chunk.length, at: new Date(now()).toISOString() },
        ],
      });
    }
    return sent;
  };

  const buffered = (session: OpsSession): SessionScan[] =>
    session.scans.filter((s) => s.status === 'buffered');

  const inFlight = (session: OpsSession, unsent: Set<string>): boolean =>
    session.batches.some((b) => unsent.has(b.commandId));

  const flushOne = (id: string): number => {
    const session = map.get(id);
    return session ? sendScans(session, buffered(session)) : 0;
  };

  /** The rule from the top of this file. */
  const maybeFlush = (session: OpsSession, unsent: Set<string>): void => {
    const waiting = buffered(session);
    if (waiting.length === 0) return;
    // A run starts once and closes once: only "Finish" makes the command.
    if (SESSION_KINDS[session.kind].singleCommand) return;
    if (waiting.length >= maxBatch) {
      // Whole commands only: the remainder keeps waiting for more scans or the finish.
      sendScans(session, waiting.slice(0, Math.floor(waiting.length / maxBatch) * maxBatch));
      return;
    }
    // A handover waits for "Finish": a damage note must be on its unit before the batch leaves.
    if (SESSION_KINDS[session.kind].holdUntilFinish) return;
    if (!isOnline() || inFlight(session, unsent)) return;
    const first = waiting[0];
    const age = first ? now() - Date.parse(first.at) : 0;
    if (waiting.length >= onlineBatch || age >= flushMs) sendScans(session, waiting);
  };

  const tick = (): void => {
    if (sending) return;
    const unsent = unsentCommandIds(writer.getSnapshot());
    for (const session of [...map.values()]) if (isInProgress(session)) maybeFlush(session, unsent);
  };

  const cleanups: (() => void)[] = [writer.subscribe(tick), connectivity.subscribe(tick)];
  if (autoTick) {
    timer = setInterval(tick, TICK_MS);
    cleanups.push(() => {
      if (timer !== undefined) clearInterval(timer);
      timer = undefined;
    });
  }

  return {
    ready,
    subscribe: store.subscribe,
    getSnapshot: store.getSnapshot,
    idle: async () => {
      await ready;
      await (draining ?? Promise.resolve());
    },
    get: (id) => map.get(id),

    start(input) {
      for (const s of map.values()) {
        if (
          isInProgress(s) &&
          s.uid === input.uid &&
          s.kind === input.kind &&
          s.targetId === input.targetId
        ) {
          return s;
        }
      }
      const at = new Date(now()).toISOString();
      return save({
        id: `ses_${ulid(now())}`,
        ...input,
        startedAt: at,
        updatedAt: at,
        scans: [],
        batches: [],
      });
    },

    addScan(id, raw, precheck) {
      const session = map.get(id);
      const barcode = raw.trim().toUpperCase();
      if (!session) {
        return { outcome: 'invalid', barcode, message: 'This session is closed', tone: 'bad' };
      }
      const info = SESSION_KINDS[session.kind];
      const valid = (info.acceptsPieces ? unitIdSchema : barcodeSchema).safeParse(barcode).success;
      if (!valid) {
        const known = session.scans.some((s) => s.barcode === barcode);
        if (!known) {
          save({
            ...session,
            scans: [
              ...session.scans,
              { barcode, at: new Date(now()).toISOString(), status: 'invalid' },
            ],
          });
        }
        return {
          outcome: 'invalid',
          barcode,
          message: info.acceptsPieces
            ? 'Not an EcoBox box barcode or piece label'
            : 'Not an EcoBox box barcode',
          tone: 'bad',
        };
      }
      const existing = session.scans.find((s) => s.barcode === barcode);
      if (existing || precheck?.code === 'already_received') {
        return { outcome: 'duplicate', barcode, message: 'Already scanned', tone: 'warn' };
      }
      if (
        info.singleCommand &&
        info.maxScans !== undefined &&
        session.scans.filter((s) => s.status !== 'invalid').length >= info.maxScans
      ) {
        return {
          outcome: 'invalid',
          barcode,
          message: `This takes at most ${info.maxScans} boxes at once.`,
          tone: 'bad',
        };
      }
      const at = new Date(now()).toISOString();
      if (precheck?.level === 'refuse') {
        save({
          ...session,
          scans: [...session.scans, { barcode, at, status: 'held', warning: precheck.message }],
        });
        return { outcome: 'held', barcode, message: precheck.message, tone: 'bad' };
      }
      const warning = precheck?.level === 'warn' ? precheck.message : undefined;
      save({
        ...session,
        scans: [
          ...session.scans,
          { barcode, at, status: 'buffered', ...(warning ? { warning } : {}) },
        ],
      });
      tick();
      return {
        outcome: 'added',
        barcode,
        message: warning ?? 'Scanned',
        tone: warning ? 'warn' : 'ok',
      };
    },

    releaseHeld(id, barcode) {
      update(id, (s) => ({
        ...s,
        scans: s.scans.map((x) =>
          x.barcode === barcode && x.status === 'held'
            ? { ...x, status: 'buffered' as const, at: new Date(now()).toISOString() }
            : x,
        ),
      }));
      tick();
    },

    setNote(id, barcode, note) {
      const session = map.get(id);
      const scan = session?.scans.find((s) => s.barcode === barcode);
      if (!session || !scan || scan.status === 'queued') return false;
      const text = note.trim().slice(0, CONDITION_NOTE_MAX);
      update(id, (s) => ({
        ...s,
        scans: s.scans.map((x) => {
          if (x.barcode !== barcode) return x;
          const { note: _old, ...rest } = x;
          return text === '' ? rest : { ...rest, note: text };
        }),
      }));
      return true;
    },

    removeScan(id, barcode) {
      const session = map.get(id);
      const scan = session?.scans.find((s) => s.barcode === barcode);
      if (!session || !scan) return;
      const info = SESSION_KINDS[session.kind];
      if (scan.status === 'queued') {
        if (!info.undo) return;
        try {
          writer.send(
            info.undo,
            { [info.idKey]: session.targetId, [info.itemsKey]: [barcode] },
            { occurredAtMs: now() },
          );
        } catch (err) {
          save({ ...session, lastError: err instanceof Error ? err.message : String(err) });
          return;
        }
      }
      update(id, (s) => ({ ...s, scans: s.scans.filter((x) => x.barcode !== barcode) }));
    },

    setExtra(id, key, value) {
      update(id, (s) => ({ ...s, extra: { ...s.extra, [key]: value } }));
    },

    flush: flushOne,

    flushAll(uid) {
      let sent = 0;
      for (const session of [...map.values()]) {
        if (session.uid === uid && isInProgress(session)) sent += flushOne(session.id);
      }
      return sent;
    },

    retrySingly(id, commandId) {
      const session = map.get(id);
      if (!session) return 0;
      const failed = session.scans.filter((s) => s.commandId === commandId);
      const reset = new Set(failed.map((s) => s.barcode));
      const next = save({
        ...session,
        scans: session.scans.map((s) =>
          reset.has(s.barcode) ? { ...s, status: 'buffered' as const } : s,
        ),
      });
      // One command per box, whatever the connection: this is the diagnosis path.
      let sent = 0;
      for (const scan of failed) {
        const fresh = map.get(next.id);
        if (!fresh) break;
        const one = fresh.scans.find((s) => s.barcode === scan.barcode && s.status === 'buffered');
        if (one) sent += sendScans(fresh, [one]);
      }
      return sent;
    },

    markClosed(id, commandId) {
      update(id, (s) => ({
        ...s,
        closeCommandId: commandId,
        finishedAt: new Date(now()).toISOString(),
      }));
    },

    complete(id) {
      update(id, (s) => ({ ...s, finishedAt: new Date(now()).toISOString() }));
    },

    reopen(id) {
      update(id, (s) => {
        const { finishedAt: _done, closeCommandId: _command, ...rest } = s;
        if (!SESSION_KINDS[s.kind].singleCommand) return rest;
        // A run's one command was refused: its boxes go back to "not sent yet" so the person can
        // fix the list and finish again with a new command.
        const refused = new Set(s.batches.map((b) => b.commandId));
        return {
          ...rest,
          scans: rest.scans.map((scan) => {
            if (scan.status !== 'queued' || !scan.commandId || !refused.has(scan.commandId)) {
              return scan;
            }
            const { commandId: _id, ...kept } = scan;
            return { ...kept, status: 'buffered' as const };
          }),
          batches: [],
        };
      });
    },

    discard(id) {
      const session = map.get(id);
      if (!session || buffered(session).length > 0 || session.batches.length > 0) return false;
      map.delete(id);
      removed.add(id);
      publish();
      void drain();
      return true;
    },

    clearUser(uid) {
      for (const session of [...map.values()]) {
        if (session.uid !== uid) continue;
        map.delete(session.id);
        dirty.delete(session.id);
        removed.add(session.id);
      }
      publish();
      void drain();
    },

    tick,

    dispose() {
      for (const c of cleanups.splice(0)) c();
    },
  };
}
